import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchCollection, fetchNewsBySlug, getOptimizedImageUrl } from '../lib/cms';
import { ArrowLeft, ChevronRight, Search, Calendar, Tag, User } from 'lucide-react';

export default function News({ isEn }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const lang = isEn ? 'en' : 'da';

  const [newsList, setNewsList] = useState([]);
  const [currentNews, setCurrentNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveFilter] = useState('all');

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        if (slug) {
          const item = await fetchNewsBySlug(slug);
          if (item) {
            setCurrentNews(item);
          } else {
            navigate(isEn ? '/en/404' : '/404');
          }
        } else {
          const items = await fetchCollection('news', { limit: 100 });
          setNewsList(items);
          setCurrentNews(null);
        }
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug, navigate, isEn]);

  // Filtrering og søgning af nyheder
  const filteredNews = newsList.filter(item => {
    const title = item.title?.[lang]?.toLowerCase() || '';
    const content = item.content?.[lang]?.toLowerCase() || '';
    const category = item.category?.[lang]?.toLowerCase() || '';
    
    const matchesSearch = title.includes(searchQuery.toLowerCase()) || content.includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || category.includes(activeCategory.toLowerCase());
    
    return matchesSearch && matchesCategory;
  });

  // 1. DETALJESIDE
  if (slug && currentNews) {
    const title = currentNews.title?.[lang] || '';
    const content = currentNews.content?.[lang] || '';
    const category = currentNews.category?.[lang] || '';
    const date = currentNews.publish_date || '';
    const seoTitle = currentNews.seo_title?.[lang] || `${title} - Fredericia Isolerings-puder`;
    const seoDesc = currentNews.seo_description?.[lang] || '';

    return (
      <>
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDesc} />
        </Helmet>

        <article className="py-12 sm:py-20 bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link
              to={isEn ? '/en/news' : '/nyheder'}
              className="inline-flex items-center gap-2 text-sm font-mono text-black hover:text-brand-accent transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              {isEn ? 'Back to News' : 'Tilbage til nyheder'}
            </Link>

            {/* Header */}
            <div className="space-y-4 text-left mb-8">
              <div className="flex flex-wrap items-center gap-4 text-xs font-mono text-gray-500">
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5 text-brand-accent" />
                  {date}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1.5">
                  <Tag className="h-3.5 w-3.5 text-brand-accent" />
                  {category}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-brand-accent" />
                  wordpress@itbot.dk
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 tracking-tight leading-tight">
                {title}
              </h1>
            </div>

            {/* Main Image */}
            {currentNews.image?.[0] && (
              <div className="aspect-[16/10] rounded-none overflow-hidden border border-gray-200 shadow-sm mb-12">
                <img
                  src={getOptimizedImageUrl(currentNews.image[0], { width: 1000 })}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content */}
            <div 
              className="text-gray-700 space-y-6 leading-relaxed text-sm sm:text-base prose text-left"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
        </article>
      </>
    );
  }

  // 2. OVERSIGTSSIDE
  const seoTitle = isEn ? 'News & Insights - Fredericia Isolerings-puder' : 'Nyheder - Fredericia Isolerings-puder';
  const seoDesc = isEn 
    ? 'Read the latest news and articles from Fredericia Isolerings-puder.' 
    : 'Læs de seneste nyheder og artikler fra Fredericia Isolerings-puder.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
      </Helmet>

      <section className="py-12 sm:py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 tracking-tight">
              {isEn ? 'News & Insights' : 'Nyheder'}
            </h1>
          </div>

          {/* Search & Filter Controls */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12 border-b border-gray-200 pb-8">
            {/* Search Input */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isEn ? 'Search articles...' : 'Søg i artikler...'}
                className="w-full bg-white border border-gray-300 rounded-none pl-10 pr-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-brand-accent transition-colors"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-3">
              <button
                onClick={() => setActiveFilter('all')}
                className={`px-4 py-2 rounded-none text-xs font-mono uppercase tracking-wider border transition-all duration-200 ${
                  activeCategory === 'all'
                    ? 'bg-black border-black text-white font-bold'
                    : 'border-gray-300 hover:border-gray-400 text-gray-600 hover:text-black'
                }`}
              >
                {isEn ? 'All' : 'Alle'}
              </button>
              <button
                onClick={() => setActiveFilter('blog')}
                className={`px-4 py-2 rounded-none text-xs font-mono uppercase tracking-wider border transition-all duration-200 ${
                  activeCategory === 'blog'
                    ? 'bg-black border-black text-white font-bold'
                    : 'border-gray-300 hover:border-gray-400 text-gray-600 hover:text-black'
                }`}
              >
                Blog
              </button>
            </div>
          </div>

          {/* News Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent" />
            </div>
          ) : filteredNews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              {filteredNews.map((item) => (
                <Link
                  key={item.id}
                  to={isEn ? `/en/news/${item.url_slug}` : `/nyheder/${item.url_slug}`}
                  className="bg-white border border-gray-200 rounded-none p-6 flex flex-col sm:flex-row gap-6 hover:border-gray-300 transition-all duration-300 group shadow-sm"
                >
                  <div className="w-full sm:w-48 aspect-[16/10] sm:aspect-square shrink-0 rounded-none overflow-hidden bg-gray-100">
                    {item.image?.[0] ? (
                      <img
                        src={getOptimizedImageUrl(item.image[0], { width: 300 })}
                        alt={item.title?.[lang]}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 font-mono text-xs">
                        {isEn ? 'No Image' : 'Intet billede'}
                      </div>
                    )}
                  </div>
                  <div className="flex-grow flex flex-col justify-between py-1 space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 text-xs font-mono text-gray-500">
                        <span>{item.publish_date}</span>
                        <span>•</span>
                        <span className="text-brand-accent">{item.category?.[lang]}</span>
                      </div>
                      <h3 className="font-display font-bold text-lg text-gray-900 group-hover:text-black transition-colors line-clamp-2">
                        {item.title?.[lang]}
                      </h3>
                      <div 
                        className="text-xs text-gray-600 line-clamp-2 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.content?.[lang] || '' }}
                      />
                    </div>
                    <div className="text-xs font-semibold text-black flex items-center gap-1 group-hover:text-gray-600 transition-colors">
                      {isEn ? 'Read Article' : 'Læs artiklen'}
                      <ChevronRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-gray-500 font-mono text-xs">
              {isEn ? 'No articles found.' : 'Ingen artikler fundet.'}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
